import "@fontsource/montserrat/latin-400.css";
import "@fontsource/montserrat/latin-500.css";
import "@fontsource/poppins/latin-400.css";
import "@fontsource/poppins/latin-500.css";
import "@fontsource/poppins/latin-700.css";

import { PaletteMode, ThemeOptions } from "@mui/material/styles";
import { color } from "./color";
import { lightSemanticColors, darkSemanticColors, SemanticColors } from "./semanticColors";
import hex2rgba from "hex2rgba";

interface AdminColors {
    main: string;
}

// MUI Theme-Typen erweitern, um unsere semantischen Farben einzuschließen
declare module "@mui/material/styles" {
    interface Palette {
        semantic: SemanticColors;
    }

    interface PaletteOptions {
        semantic?: SemanticColors;
        admin?: AdminColors;
    }
}

// Add custom variant "nospace" for Typography and Link
declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        nospace: true;
    }
}

declare module "@mui/material/Link" {
    interface LinkPropsVariantOverrides {
        nospace: true;
    }
}

/**
 * Definiert das anwendungsweite Theming der UI.
 * In den Komponenten selbst soll nur noch minimales Styling nötig sein.
 *
 * https://mui.com/material-ui/customization/theme-components/
 */
export const getTheme = (mode: PaletteMode): ThemeOptions => {
    const semanticColors = mode === "light" ? lightSemanticColors : darkSemanticColors;

    return {
        // https://mui.com/material-ui/customization/palette/
        palette: {
            mode,

            /* --- brand colours --- */
            primary: {
                main: color.brand.one,
            },

            admin: {
                main: color.brand.one,
            },

            secondary: {
                main: color.brand.two,
            },

            /* --- NEW defaults for the feedback colours --- */
            success: {
                main:  color.brand.one,
                // light: color.green.light,
                // dark:  color.green.dark,
                // contrastText: "#fff",
            },
            error: {
                main:  color.red.dark,
                // light: "#FEE4E2",
                // dark:  "#B42318",
                // contrastText: "#fff",
            },
            warning: {
                main:  color.orange.dark,
                // light: "#FEF0C7",
                // dark:  "#B54708",
                // contrastText: "#fff",
            },

            text: {
                primary: semanticColors.text.paragraph,
            },
            background: {
                paper: color.brand.background,
                default: color.brand.background,
            },
            info: {
                main: color.brand.two,
                dark: color.brand.two,
            },
            // Unsere semantischen Farben zur Palette hinzufügen
            semantic: semanticColors,
        },

        // https://mui.com/material-ui/customization/typography/
        typography: {
            fontFamily: "Montserrat",

            body1: {
                fontSize: 14,
                color: semanticColors.text.paragraph, // Semantische Farbe verwenden
            },

            // Z.B. Überschrift HeroSection
            h1: {
                fontFamily: "Poppins",
                fontWeight: 600,
                color: semanticColors.text.heading, // Semantische Farbe verwenden
            },

            h2: {
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "3rem",
                color: semanticColors.text.heading, // Semantische Farbe verwenden
            },

            h3: {
                fontFamily: "Poppins",
                fontSize: "2rem",
                fontWeight: 500,
                color: semanticColors.text.heading, // Semantische Farbe verwenden
            },

            h4: {
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 24,
                color: semanticColors.text.heading, // Semantische Farbe verwenden
            },

            h5: {
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 19,
                color: semanticColors.text.heading, // Semantische Farbe verwenden
            },
        },

        // https://mui.com/material-ui/customization/breakpoints/
        breakpoints: {
            values: { xs: 0, sm: 750, md: 900, lg: 1200, xl: 1536 },
        },

        shape: {
            borderRadius: 14,
        },

        // https://mui.com/material-ui/customization/theme-components/
        components: {
            MuiCssBaseline: {
                styleOverrides: {},
            },

            MuiSkeleton: {
                defaultProps: {
                    variant: "rounded",
                },
            },

            MuiAppBar: {
                defaultProps: {
                    position: "fixed",
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        // background: hexToRgba(color.green.dark, 0.2),
                        background: "transparent",
                        backdropFilter: "blur(5px)",
                    },
                },
            },

            MuiToolbar: {
                styleOverrides: {
                    root: {
                        marginInline: "6px",
                        paddingBlock: 16, // Anpassung für Desktops
                    },
                },
            },

            MuiButton: {
                defaultProps: {
                    // Remove color default - let components specify their own
                },
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        ...(ownerState.variant === "text" && {
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            color: theme.palette.semantic.text.heading,
                            "&:hover": {
                                backgroundColor: `${theme.palette.semantic.button.primary}20`,
                                color: theme.palette.semantic.button.primaryHover,
                            },
                        }),

                        ...(ownerState.variant === "contained" && {
                            fontWeight: 500,
                            background: theme.palette.semantic.button.gradient,
                            backgroundSize: "200% 100%",
                            backgroundPosition: "0% 0%",
                            transition: "background-position 0.3s ease-in-out",
                            "&:hover": {
                                backgroundPosition: "100% 0%",
                            },
                            "&:disabled": {
                                backgroundColor: theme.palette.semantic.button.disabled,
                                backgroundImage: "none",
                            },
                        }),

                        ...(ownerState.variant === "outlined" && {
                            borderColor: theme.palette.semantic.button.outline,
                            color: theme.palette.semantic.button.outline,
                            background: "transparent",
                            "&:hover": {
                                borderColor: theme.palette.semantic.button.outlineHover,
                                color: theme.palette.semantic.button.outlineHover,
                                background: `${theme.palette.semantic.button.primary}15`,
                            },
                        }),
                    }),
                },
            },

            MuiCard: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        background: theme.palette.semantic.surface.primary,
                    }),
                },
            },

            MuiCardHeader: {
                defaultProps: {
                    titleTypographyProps: {
                        variant: "body1",
                        fontSize: "1rem",
                        fontWeight: 600,
                    },
                    subheaderTypographyProps: {
                        variant: "body2",
                    },
                },
                styleOverrides: {
                    root: {},
                },
            },

            MuiCardActions: {
                defaultProps: {},
                styleOverrides: {
                    root: {
                        marginBottom: 2,
                        marginLeft: 2,
                    },
                },
            },

            MuiCardMedia: {
                defaultProps: {},
                styleOverrides: {
                    root: {},
                },
            },

            MuiSvgIcon: {
                defaultProps: {
                    color: "inherit",
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: "inherit",
                        color: theme.palette.semantic.icon.default,
                        
                        // Allow icons to inherit color from contained buttons
                        ".MuiButton-contained &": {
                            color: "inherit",
                        },
                    }),
                },
            },

            MuiContainer: {
                defaultProps: {
                    maxWidth: "lg",
                },
            },

            MuiAvatar: {
                styleOverrides: {
                    root: {
                        width: 42,
                        height: 42,
                        cursor: "pointer",
                        color: color.brand.one,
                        fontWeight: 500,
                    },
                },
            },

            MuiLink: {
                defaultProps: {
                    target: "_blank",
                    rel: "noopener noreferrer",
                    variant: "body2",
                    color: "semantic.text.paragraph",
                    fontStyle: "italic",
                },
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        bgcolor: "transparent !important",
                        backgroundColor: "transparent !important",
                        // Default spacing for links
                        marginLeft: "40px !important",

                        // Remove spacing for custom variant
                        ...(ownerState.variant === "nospace" && {
                            marginLeft: 0,
                        }),
                    }),
                },
            },

            MuiLinearProgress: {
                styleOverrides: { root: { borderRadius: 8 } },
            },

            MuiTextField: {
                defaultProps: {
                    variant: "outlined",
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        "& .MuiInputBase-input": {
                            paddingLeft: 16,
                        },
                        "& .MuiOutlinedInput-root": {
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.semantic.navigation.border,
                            },
                        },
                        "& .MuiInputLabel-root": {
                            "&.Mui-focused": {
                                color: theme.palette.semantic.navigation.border,
                            },
                        },
                    }),
                },
            },

            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 6,
                        boxShadow: "none",
                        border: "1px solid",
                    },
                },
            },

            MuiDrawer: {
                defaultProps: {
                    anchor: "right",
                },
            },

            MuiSwitch: {
                defaultProps: {
                    color: "secondary",
                },
            },

            MuiSnackbar: {
                defaultProps: {
                    autoHideDuration: 6000,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                },
                styleOverrides: {
                    root: {},
                },
            },

            MuiAlert: {
                defaultProps: {
                    variant: "filled",
                    slotProps: {
                        closeIcon: {
                            sx: {
                                fontSize: "2rem",
                            },
                        },
                    },
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: "0.9rem",
                        fontWeight: 400,
                        alignItems: "center",
                        opacity: 1,
                        "&.MuiAlert-filledSuccess": {
                            backgroundColor: hex2rgba(theme.palette.semantic.notification.success, 0.75),
                            color: theme.palette.semantic.text.paragraph,
                            "& .MuiAlert-icon": {
                                color: theme.palette.semantic.notification.success,
                            },
                        },
                        "&.MuiAlert-filledError": {
                            backgroundColor: hex2rgba(theme.palette.semantic.notification.error, 0.25),
                            color: theme.palette.semantic.text.paragraph,
                            "& .MuiAlert-icon": {
                                color: theme.palette.semantic.notification.error,
                            },
                        },
                        "&.MuiAlert-filledWarning": {
                            backgroundColor: hex2rgba(theme.palette.semantic.notification.warning, 0.25),
                            color: theme.palette.semantic.text.paragraph,
                            "& .MuiAlert-icon": {
                                color: theme.palette.semantic.notification.warning,
                            },
                        },
                        "&.MuiAlert-filledInfo": {
                            backgroundColor: hex2rgba(theme.palette.semantic.notification.info, 0.25),
                            color: theme.palette.semantic.text.paragraph,
                            "& .MuiAlert-icon": {
                                color: theme.palette.semantic.notification.info,
                            },
                        },
                    }),
                },
            },

            MuiAlertTitle: {
                defaultProps: {},
                styleOverrides: {
                    root: {
                        fontSize: "1.7rem",
                        fontWeight: "bold",
                    },
                },
            },

            MuiIconButton: {
                defaultProps: {
                    // Remove color default - let components specify their own
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: theme.palette.semantic.icon.functional,
                    }),
                },
            },

            MuiListItemText: {
                defaultProps: {
                    primaryTypographyProps: {
                        variant: "body1",
                        fontSize: "1.1rem",
                    },
                },
                styleOverrides: {
                    root: {},
                },
            },

            MuiTooltip: {
                defaultProps: {},
                styleOverrides: {
                    tooltip: ({ theme }) => ({
                        whiteSpace: "pre-line",
                        fontSize: "0.75rem",
                        borderRadius: 6,
                        backgroundColor: theme.palette.semantic.navigation.button,
                    }),
                },
            },

            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        paddingRight: 12,
                        paddingBottom: 12,
                    },
                },
            },

            MuiToggleButtonGroup: {
                styleOverrides: {
                    root: {
                        height: 28,
                    },
                },
            },

            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        paddingInline: 12,
                        fontSize: 12,
                    },
                },
            },

            MuiStepIcon: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: "2rem",
                        color: "transparent", // Kreis ungefüllt
                        border: `1px solid ${theme.palette.semantic.navigation.border}`,
                        borderRadius: "50%",

                        /* ---------- Aktiver Schritt ---------- */
                        "&.Mui-active": {
                            color: theme.palette.semantic.navigation.button,
                            border: "none",

                            /* >>> Ziffer innerhalb des aktiven Icons <<< */
                            "& .MuiStepIcon-text": {
                                fill: color.green.light, // helle Schrift
                            },
                        },

                        /* ---------- Abgeschlossene Schritte ---------- */
                        "&.Mui-completed": {
                            color: theme.palette.semantic.navigation.button,
                            border: "none",

                            "& .MuiStepIcon-text": {
                                fill: theme.palette.semantic.navigation.button,
                            },
                        },
                    }),

                    /* Standardbeschriftung für inaktive Schritte */
                    text: ({ theme }) => ({
                        fill: theme.palette.semantic.navigation.border,
                    }),
                },
            },

            MuiStepLabel: {
                styleOverrides: {
                    label: ({ theme }) => ({
                        // Style for the step label text
                        "&.Mui-active": {
                            color: theme.palette.semantic.navigation.button,
                        },
                        "&.Mui-completed": {
                            color: theme.palette.semantic.navigation.button,
                        },
                    }),
                },
            },
        },

        // https://mui.com/material-ui/customization/transitions/
        transitions: {},
    };
};
